<script lang="ts">
	import { _ } from "svelte-i18n";
	import { Button, FormGroup, Input, Label } from "@sveltestrap/sveltestrap";
	import type { DropdownOption } from "../../../common/types";
	import UploadOrRecord from "./UploadOrRecord.svelte";
	import {
		CUSTOM_PERSONA_ID,
		type CustomVoice,
		type DraftCustomPersona,
	} from "./customPersonaTypes.ts";
	import { validateDraft } from "./customPersonaLogic.ts";

	let {
		initial = null,
		availableVoices = [],
		onSave,
		onCancel,
	}: {
		initial?: DraftCustomPersona | null;
		availableVoices?: DropdownOption[];
		onSave?: (draft: DraftCustomPersona) => void;
		onCancel?: () => void;
	} = $props();

	let name = $state(initial?.name ?? "");
	let label = $state(initial?.label ?? "");
	let personality = $state(initial?.personality ?? "");
	let voiceMode = $state<"preset" | "clone">(
		initial?.voice.kind === "clone" || availableVoices.length === 0 ? "clone" : "preset",
	);
	let presetVoiceId = $state(
		initial && initial.voice.kind === "preset"
			? initial.voice.voiceId
			: (availableVoices[0]?.value ?? ""),
	);
	let voiceSampleBase64 = $state(
		initial && initial.voice.kind === "clone" ? initial.voice.sampleBase64 : "",
	);
	let imageBase64 = $state(initial?.imageBase64 ?? "");

	const presetsAvailable = $derived(availableVoices.length > 0);

	const voice = $derived(
		(voiceMode === "clone"
			? { kind: "clone", sampleBase64: voiceSampleBase64 }
			: { kind: "preset", voiceId: presetVoiceId }) as CustomVoice,
	);

	const draft = $derived({
		isCustom: true as const,
		id: CUSTOM_PERSONA_ID,
		name: name.trim(),
		label: label.trim(),
		personality: personality.trim(),
		voice,
		...(imageBase64 ? { imageBase64 } : {}),
	} as DraftCustomPersona);

	const validation = $derived(validateDraft(draft));
	const canSave = $derived(validation.ok);

	function onSubmit() {
		if (!validation.ok) return;
		onSave?.(draft);
	}

	function handleCancel() {
		onCancel?.();
	}
</script>

<div class="custom-persona-form">
	<h5 class="form-title">
		{$_("landingpage.newPresentation.customPersona.formTitle")}
	</h5>

	<FormGroup>
		<Label for="cp-name">
			{$_("landingpage.newPresentation.customPersona.nameLabel")}
		</Label>
		<Input id="cp-name" type="text" bind:value={name} />
		{#if !validation.ok && validation.errors.name}
			<div class="field-error">
				{$_("landingpage.newPresentation.customPersona.validationRequired")}
			</div>
		{/if}
	</FormGroup>

	<FormGroup>
		<Label for="cp-label">
			{$_("landingpage.newPresentation.customPersona.labelLabel")}
		</Label>
		<Input id="cp-label" type="text" bind:value={label} />
		{#if !validation.ok && validation.errors.label}
			<div class="field-error">
				{$_("landingpage.newPresentation.customPersona.validationRequired")}
			</div>
		{/if}
	</FormGroup>

	<FormGroup>
		<Label for="cp-personality">
			{$_("landingpage.newPresentation.customPersona.personalityLabel")}
		</Label>
		<Input id="cp-personality" type="textarea" rows={4} bind:value={personality} />
		{#if !validation.ok && validation.errors.personality}
			<div class="field-error">
				{$_("landingpage.newPresentation.customPersona.validationRequired")}
			</div>
		{/if}
	</FormGroup>

	<fieldset class="voice-section">
		<legend>
			{$_("landingpage.newPresentation.customPersona.voiceSectionLabel")}
		</legend>

		<label class="radio-line">
			<input
				type="radio"
				name="voice-mode"
				value="preset"
				bind:group={voiceMode}
				disabled={!presetsAvailable}
			/>
			{$_("landingpage.newPresentation.customPersona.voiceModePreset")}
		</label>
		{#if !presetsAvailable}
			<div class="field-error">
				{$_("landingpage.newPresentation.customPersona.voicePresetsUnavailable")}
			</div>
		{/if}

		{#if voiceMode === "preset"}
			<FormGroup>
				<Label for="cp-voice-preset">
					{$_("landingpage.newPresentation.customPersona.voicePresetSelectLabel")}
				</Label>
				<Input id="cp-voice-preset" type="select" bind:value={presetVoiceId}>
					{#each availableVoices as voice}
						<option value={voice.value}>{voice.label}</option>
					{/each}
				</Input>
			</FormGroup>
		{/if}

		<label class="radio-line">
			<input type="radio" name="voice-mode" value="clone" bind:group={voiceMode} />
			{$_("landingpage.newPresentation.customPersona.voiceModeClone")}
		</label>

		{#if voiceMode === "clone"}
			<div class="hint">
				{$_("landingpage.newPresentation.customPersona.voiceSampleHint")}
			</div>
			<UploadOrRecord mediaKind="audio" bind:value={voiceSampleBase64} />
		{/if}
		{#if !validation.ok && validation.errors.voice}
			<div class="field-error">
				{$_("landingpage.newPresentation.customPersona.validationRequired")}
			</div>
		{/if}
	</fieldset>

	<fieldset>
		<legend>
			{$_("landingpage.newPresentation.customPersona.imageSectionLabel")}
		</legend>
		<div class="hint">
			{$_("landingpage.newPresentation.customPersona.imageOptional")}
		</div>
		<UploadOrRecord mediaKind="image" bind:value={imageBase64} />
	</fieldset>

	<div class="actions">
		<Button color="secondary" on:click={handleCancel}>
			{$_("landingpage.newPresentation.customPersona.back")}
		</Button>
		<Button color="primary" disabled={!canSave} on:click={onSubmit}>
			{$_("landingpage.newPresentation.customPersona.saveAndUse")}
		</Button>
	</div>
</div>

<style>
	.custom-persona-form {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}
	.form-title {
		margin: 0;
	}
	.voice-section,
	fieldset {
		border: 1px solid var(--bs-border-color, #dee2e6);
		border-radius: 0.5rem;
		padding: 0.75rem 1rem;
	}
	fieldset legend {
		font-size: 0.875rem;
		font-weight: 600;
		padding: 0 0.25rem;
		width: auto;
		margin-bottom: 0.5rem;
	}
	.radio-line {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 0.5rem;
		cursor: pointer;
	}
	.hint {
		font-size: 0.8125rem;
		color: var(--bs-secondary, #6c757d);
		margin-bottom: 0.5rem;
	}
	.field-error {
		font-size: 0.8125rem;
		color: var(--bs-danger, #dc3545);
		margin-top: 0.25rem;
	}
	.actions {
		display: flex;
		justify-content: flex-end;
		gap: 0.5rem;
	}
</style>
